import Analytics from '../analytics';
import VZThirdParty from '../lib/verizonThirdParty';
import { getLogger } from '../logging/logger';

/**
 * @typedef {object} VerizonChannelData
 * @property {string} [channelname] The name for the channel, typically the network
 * @property {string} [channelnumber] The number for the channel to check entitlements for
 */

const VzLink = /(?:vz:\/\/\/?)(\w+)\/?([\w|\s|\d]+)?\/?([\w|\s|\d]+)?\/?([\w|\s|\d]+)?/;
const logger = getLogger('Verizon');

class VerizonApi {
	constructor() {
		logger.log('[constructor]');

		document.addEventListener('click', this.onLinkClick.bind(this));

		if (this.serviceIsAvailable()) {
			try {
				VZThirdParty.Init();
			} catch (error) {
				logger.error('[VZThirdParty.Init() Failed]', error.toString());
				Analytics.sendEvent('VerizoneThirdParty-Error', {
					error: `[Initialization error] ${error.toString()}`,
				});
			}
		}
	}

	/**
	 * Checks to see if the Verizon API Services is available to be used indicating we
	 * can use Verizon STB functionality.
	 * @returns {boolean} Boolean value indicating if the Verizon API Service is detected.
	 */
	serviceIsAvailable() {
		return Object.prototype.hasOwnProperty.call(window, '__VZThirdParty');
	}

	/**
	 * Listens for click events on the DOM body
	 * @param {Event} event DOMEvent for click event
	 */
	onLinkClick(event) {
		logger.log('[onLinkClick]', { event });

		const isLink = event.target?.nodeName === 'A';
		const url = event.target?.href;
		const isVerizonLink = VzLink.test(url);

		if (isLink && isVerizonLink) {
			event.preventDefault();
			this.followDeepLink(url);
		}
	}

	/**
	 * Parses the given deepLink string in the format of "vz://apiMethodName/param/param/param". It then
	 * breaks the string apart in to the method and parameters and calls the specified method from the
	 * Verizon API.
	 * @param {string} deepLink String represented deeplink for Verizon integrations
	 * @returns {void} No return value
	 */
	followDeepLink(deepLink) {
		logger.log('[followDeepLink]', { deepLink });

		if (!this.serviceIsAvailable()) {
			const error = 'VZThirdParty Service is not available for deeplink';
			logger.error('[followDeeplink]', error);
			Analytics.sendEvent('VerizonDeepLink-Error', { error });
			return;
		}

		// Extract params and convert number params from string to int
		const [link, apiName, ...allParams] = VzLink.exec(deepLink);
		const params = allParams
			.filter((param) => ![undefined, null].includes(param))
			.map((param) => (/(Channel|Record)/.test(apiName) ? parseInt(param) : param));
		logger.debug('[onLinkClick]', { apiName, params, link });

		const apiMethod = VZThirdParty[apiName];
		if (apiMethod) {
			Analytics.sendEvent('VerizonDeepLink', { link })
				.then(() => apiMethod(...params))
				.catch((error) => {
					logger.error('[onLinkClick]', error.toString());
					Analytics.sendEvent('VerizonDeepLink-Error', {
						link,
						error: error.toString(),
					});
				});
		} else {
			const error = `Invalid API method "${apiName}"`;
			logger.error(error);
			Analytics.sendEvent('VerizonDeepLink-Error', { link, error });
		}
	}

	/**
	 * Requests the entitlment info for the specified channel name for the current Verizon STB User. 
	 * Example response:
		{
			"93": {
				"strChannelName": "GOLF",
				"strLongChNameUncomp": "Golf Channel",
				"intChannelNumber": 93,
				"intHDEquChNumber": 593,
				"intFiosServiceID": 742,
				"bIpStream": "0",
				"b4k": "0",
				"entitlement": true
			},
			"593": {
				"strChannelName": "Golf HD",
				"strLongChNameUncomp": "Golf Channel High Definition",
				"intChannelNumber": 593,
				"intHDEquChNumber": 0,
				"intFiosServiceID": 1920,
				"bIpStream": "0",
				"b4k": "0",
				"entitlement": true
			}
		}
	 * @param {VerizonChannelData} data Object containing entity to return entitlement information for.
	 * @returns {Promise<Object>} Promise that resolves an object with keys representing channel entitlment information.
	 */
	checkChannelEntitlement(data = null) {
		logger.log('[checkChannelEntitlement]', { data });

		if (!this.serviceIsAvailable()) {
			const error = `VZThirdParty Service is not available for checkChannelEntitlement: ${JSON.stringify(
				data,
			)}`;
			logger.error(error);
			Analytics.sendEvent('VerizonChannelEntitlement-Error', { error });
			return Promise.reject(error);
		}

		return new Promise((resolve) => {
			VZThirdParty.GetData('channelentitlement', data, resolve);
		})
			.then((response) => {
				const { responseData } = response;
				Analytics.sendEvent(
					'VerizonChannelEntitlement',
					JSON.stringify({ ...data, ...responseData }),
				);

				if (!responseData || !Object.keys(responseData).length) {
					// Log responses with missing or empty response data for diagnostic purposes. Seems suspicious.
					logger.debug('[checkChannelEntitlement]', { response });
					Analytics.sendEvent('VerizonChannelEntitlement', {
						status: 'suspicious response',
						data: JSON.stringify(response),
					});
				}

				return responseData;
			})
			.catch((error) => {
				logger.error('[checkChannelEntitlement]', { error });

				Analytics.sendEvent('VerizonChannelEntitlement-Error', {
					error: error.toString(),
				});

				return Promise.reject(error);
			});
	}

	/**
	 * Convience method. Checks if a user is subscribed for a channel.
	 * @param {string} channelname The channel to check subscription against.
	 * @returns {Promise<any>} Resolves if subscribed, Rejects if not subscribed.
	 */
	isSubscriber(channelname) {
		logger.log(`[${this.isSubscriber.name}]`, { channelname });

		return this.checkChannelEntitlement({ channelname }).then(
			(entitlementData) => {
				logger.log('[checkEntitlementResponse]', { entitlementData });

				const isSubscriber = Object.values(entitlementData)
					.filter(({ strChannelName }) =>
						new RegExp(channelname, 'i').test(strChannelName),
					)
					.some(({ entitlement }) => entitlement);

				Analytics.sendEvent('VerizonSubscriberCheck', {
					status: isSubscriber.toString(),
					data: channelname,
				});

				if (isSubscriber) {
					return Promise.resolve();
				}

				return Promise.reject(
					new Error(`User does not have entitlement for "${channelname}"`),
				);
			},
			(error) => {
				logger.error('[check entitlment error]', { error });
				return Promise.reject(error);
			},
		);
	}
}

export default new VerizonApi();
